import React, { useLayoutEffect, useRef, useState } from 'react';
import BemClass from '@upsales/components/Utils/bemClass';
import { ButtonGroup, Button, Icon, Title, Checkbox, Progress, IconName } from '@upsales/components';
import T from 'Components/Helpers/translate';
import FakeTooltip from 'Components/Tooltip/FakeTooltip';
import './DashboardButtonGroup.scss';
import { SlideFade } from 'App/components/animations';
import UserDefinedObjectEntity from 'App/enum/UserDefinedObjectEntity';
import { openDrawer } from 'Services/Drawer';
import Comment from 'App/babel/resources/Comment';
import Contact from 'App/resources/Model/Contact';
import Client from 'App/resources/Model/Client';
import logError from 'App/babel/helpers/logError';
import Avatar from 'Components/Avatar';
import { openNewMailWithContact } from 'App/helpers/mailHelpers';
import MentionsInput from 'Components/Mentions/MentionsInput';
import Editor from '@draft-js-plugins/editor';
import openModal from 'App/services/Modal';
import { useFeatureAvailable, useForceRender, useSoftDeployAccess } from 'App/components/hooks';
import SubAccountStateFrame from '../SubAccountStateFrame';

import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';
import { openEditOrder } from 'App/components/EditOrder';

import { openEditContact } from 'App/components/EditContact/openEditContact';
import { openEditContactRelation } from 'App/components/EditContactRelationModal/openEditContactRelation';
import openEditEsign from 'App/components/Esign/EditEsign/EditEsign';
import {
	Feature,
	Product,
	isAvailable,
	hasSoftDeployAccess,
	isAvailableProduct
} from 'App/services/FeatureHelperStandalone';
import AppService from 'App/legacy/AppService';

const VISIBLE_ACTION_COUNT = 5;
const CommentConfirm = (props: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>) => {
	return (
		<div {...props}>
			<Checkbox checked />
			<Title>{T('comment.addedBelow')}</Title>
		</div>
	);
};
type Props = {
	contactsTotal?: number;
	isContact: boolean;
	account: Client;
	// See the note in Overview: `Contact.userEditable` is a boolean, so
	// `& { userEditable: true }` was unsatisfiable. Only id/name/email are read.
	contact?: Contact;
	score?: number;
};

type Action = {
	title: string;
	icon: string;
	buttonColor?: string;
	shouldRender: boolean;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

const isDraftEditor = (ref: HTMLTextAreaElement | Editor): ref is Editor => {
	return (ref as Editor).editor !== undefined;
};
const DashboardButtonGroup = ({ account, contactsTotal, contact, isContact = false, score }: Props) => {
	const textareaRef = useRef<HTMLTextAreaElement | Editor | null>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [comment, setComment] = useState('');
	const [saving, setSaving] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const hasSubAccountsV2 = useSoftDeployAccess('SUB_ACCOUNTS_V2');
	const hasSubAccountsFeature = useFeatureAvailable(Feature.SUB_ACCOUNTS) && hasSubAccountsV2;

	const hasActivitiesAndAppointments = isAvailable(Feature.ACTIVITIES_AND_APPOINTMENTS);
	const hasTodoList = hasSoftDeployAccess('TODO_LIST');
	const hasRemoveActivities = hasSoftDeployAccess('REMOVE_ACTIVITIES');
	const hasEmail = isAvailable(Feature.EMAIL);
	const hasCompaniesAndContacts = isAvailable(Feature.COMPANIES_AND_CONTACTS);
	const hasPipeline = isAvailable(Feature.PIPELINE);
	const hasOrders = isAvailable(Feature.ORDERS);
	const hasRecurringOrder = isAvailable(Feature.RECURRING_ORDER);
	const hasSubscriptionModal = hasSoftDeployAccess('SUBSCRIPTION_MODAL');
	const hasUdo = isAvailable(Feature.UDO);
	const hasDocuments = isAvailable(Feature.DOCUMENTS);
	const hasCompanyRelations = isAvailable(Feature.COMPANY_RELATIONS);
	const hasMarketingEvents = isAvailable(Feature.MARKETING_EVENTS);
	const hasGroupMail = isAvailable(Feature.GROUP_MAIL);
	const hasLeads = isAvailable(Feature.LEADS);
	const hasMarketingAutomation = isAvailableProduct(Product.MA);
	const hasReactUploadFileModal = hasSoftDeployAccess('REACT_UPLOAD_FILE_MODAL');
	const hasEsign = isAvailable(Feature.ESIGN);
	const hasExportData = isAvailable(Feature.EXPORT_DATA);

	const isSubAccount = !!account.operationalAccount;

	const { forced, triggerRender } = useForceRender();

	useLayoutEffect(() => {
		if (textareaRef && textareaRef.current && wrapperRef && wrapperRef.current) {
			const element = isDraftEditor(textareaRef.current)
				? textareaRef.current.editor?.editorContainer
				: textareaRef.current;

			if (element) {
				element.style.height = 'inherit';
				if (element.scrollHeight < 60) {
					element.style.maxHeight = element.style.height = '40px';
				} else {
					element.style.maxHeight = element.style.height = `${element.scrollHeight}px`;
				}

				wrapperRef.current.style.height = 'inherit';
			}
		}
	}, [comment, forced]);

	const saveComment = async () => {
		setSaving(true);
		const { id, name } = AppService.getSelf();
		await Comment.save({
			user: { id, name },
			description: comment,
			client: { id: account.id, name: account.name },
			contact: contact ? { id: contact.id, name: contact.name, email: contact.email } : null
		});
		setSaving(false);
		setShowConfirm(true);
		setTimeout(() => {
			setShowConfirm(false);
			setComment('');
			triggerRender();
		}, 2000);
	};
	const meta = AppService.getMetadata();

	function addMailToContact(sendEmail: (email: string) => void) {
		// eslint-disable-next-line promise/catch-or-return
		if (!contact) {
			return;
		}
		return Tools.$upModal
			.open('infoPrompt', {
				title: 'default.enterEmail',
				placeholder: 'default.email',
				required: true,
				type: 'email'
			})
			.then(function (email) {
				if (email) {
					Tools.Contact.customer(AppService.getCustomerId())
						.save({ id: contact.id, email: email })
						.then(function () {
							sendEmail(email);
						})
						.catch(e => {
							logError(e, 'Failed saving contact');
						});
				}
			});
	}

	const actions: Action[] = [
		{
			title: T('default.comment'),
			icon: 'comment',
			buttonColor: 'white',
			shouldRender: true
		},
		{
			title: T('default.activity'),
			icon: 'activity',
			shouldRender:
				account.createRights.Activity && hasActivitiesAndAppointments && !hasTodoList && !hasRemoveActivities,
			onClick: () => {
				const activity = {
					client: { id: account.id },
					contacts: contact ? { id: contact.id } : undefined
				};
				Tools.$upModal.open('editActivity', { activity });
			}
		},
		{
			title: T('todo.todo'),
			icon: 'activity',
			shouldRender: account.createRights.Activity && hasActivitiesAndAppointments && hasTodoList,
			onClick: () => openDrawer('CreateTodo', { client: account, contact })
		},
		{
			title: T('todo.planACall'),
			icon: 'phone',
			shouldRender: account.createRights.Activity && hasActivitiesAndAppointments && hasTodoList,
			onClick: () => openDrawer('CreateCall', { client: account, contact })
		},
		{
			title: T('default.appointment'),
			icon: 'calendar',
			shouldRender: account.createRights.Appointment && hasActivitiesAndAppointments,
			onClick: () =>
				openEditAppointment({
					appointment: { client: { id: account.id }, contacts: contact ? [contact] : [] }
				})
		},
		{
			title: T('default.email'),
			icon: 'envelope',
			shouldRender: hasEmail && isContact,
			onClick: () => {
				if (!contact) {
					return;
				}
				if (contact.email) {
					openNewMailWithContact(contact);
				} else {
					addMailToContact(email => {
						contact.email = email;
						openNewMailWithContact(contact);
					});
				}
			}
		},
		{
			title: T('default.contact'),
			icon: 'user',
			shouldRender: !isContact && account.createRights.Activity && hasCompaniesAndContacts && !isContact,
			onClick: () => openEditContact({ customerId: AppService.getCustomerId(), account: account })
		},
		{
			title: T('default.opportunity'),
			icon: 'opportunity',
			shouldRender: account.createRights.Opportunity && hasPipeline,
			onClick: () =>
				openEditOrder(
					contact
						? {
								customerId: AppService.getCustomerId(),
								clientId: contact.client.id,
								contactId: contact.id,
								type: 'opportunity'
						  }
						: {
								customerId: AppService.getCustomerId(),
								clientId: account.id,
								type: 'opportunity'
						  }
				)
		},
		{
			title: T('default.order'),
			icon: 'usd',
			shouldRender: account.createRights.Order && hasOrders,
			onClick: () =>
				openEditOrder({
					customerId: AppService.getCustomerId(),
					clientId: contact?.client.id || account.id,
					contactId: contact?.id
				})
		},
		{
			title: T('default.recurringOrder'),
			icon: 'dollar',
			shouldRender:
				account.createRights.Agreement &&
				meta.params.AgreementEnabled &&
				hasRecurringOrder &&
				AppService.getAccessRights().Agreement,
			onClick: () => {
				if (!hasSubscriptionModal) {
					return Tools.$upModal.open('editAgreement', {
						customerId: AppService.getCustomerId(),
						accountId: contact?.client.id || account.id,
						contactId: contact?.id
					});
				}

				openModal('CreateSubscription', {
					client: contact?.client || account,
					contact: contact,
					createdFrom: isContact ? 'contactButtonGroup' : 'accountButtonGroup'
				});
			}
		},
		...(
			meta.params.UserDefinedObject?.filter(
				udo => udo.link === (isContact ? UserDefinedObjectEntity.CONTACT : UserDefinedObjectEntity.CLIENT)
			) || []
		).map(u => ({
			title: u.name,
			icon: 'puzzle-piece',
			shouldRender: account.createRights[('UserDefObj' + u.id) as keyof typeof account.createRights] && hasUdo,
			onClick: () => {
				openModal('EditUDO', {
					typeId: u.id,
					object: {
						contact,
						client: { id: account.id, name: account.name }
					}
				});
			}
		})),
		{
			title: T('default.document'),
			icon: 'file',
			shouldRender: account.userEditable && !!AppService.getDocumentTemplates('client').length && hasDocuments,
			onClick: () => {
				openModal('CreateDocumentModal', {
					type: 'account',
					id: account.id,
					templates: AppService.getDocumentTemplates('client'),
					accountId: account.id
				});
			}
		},
		{
			title: T('account.addRelation'),
			icon: 'sitemap',
			shouldRender:
				!isContact && account.userEditable && hasCompanyRelations && !hasSubAccountsFeature && !isSubAccount,
			onClick: () => {
				openModal('AccountRelationModal', {
					account: account,
					customerId: AppService.getCustomerId()
				} as any);
			}
		},
		{
			title: T('account.mergeClients'),
			icon: 'exchange',
			shouldRender: !isContact && account.userEditable,
			onClick: () => {
				openModal('MergeClients', {
					id: account.id,
					customerId: AppService.getCustomerId(),
					meta: { account: { data: account } }
				});
			}
		},
		{
			title: T('default.marketEvent'),
			icon: 'tachometer',
			shouldRender: hasMarketingEvents,
			onClick: () => openModal('NewMarketEvent', { account, contact })
		},
		{
			title: T('default.unsubFromMassMail'),
			icon: 'minus-circle',
			shouldRender: isContact && meta.map.mail && !contact?.unsubscribed && hasGroupMail,
			onClick: () => {
				if (!contact) {
					return;
				}

				openModal('Alert', {
					title: 'default.unsubFromMassMail',
					body: 'confirm.unsubBody',
					confirmButtonText: 'default.unsubFromMassMail',
					headerIcon: 'warning',
					onClose: confirmed => {
						if (confirmed) {
							Tools.Contact.unsubscribe(contact);
						}
					}
				});
			}
		},
		{
			title: T('default.moveToAccount'),
			icon: 'truck',
			shouldRender: isContact && !!contact?.userEditable,
			onClick: () => {
				openModal('MoveContact', {
					contact: contact as Contact
				});
			}
		},
		{
			title: T('default.relation'),
			icon: 'sitemap',
			shouldRender: isContact && hasCompanyRelations,

			onClick: () => {
				if (contact) {
					openEditContactRelation({ contact });
				}
			}
		},
		{
			title: T('contact.mergeWithOtherContact'),
			icon: 'exchange',
			shouldRender: isContact && !!contact?.userEditable,
			onClick: () => {
				if (!contact) {
					return;
				}
				return openModal('MergeContacts', {
					id: contact.id,
					accountId: contact.client.id,
					customerId: AppService.getCustomerId()
				});
			}
		},
		{
			title: T('default.resubForMassMail'),
			icon: 'minus-circle',
			shouldRender:
				isContact &&
				meta.map.mail &&
				(AppService.getSelf().administrator || !!AppService.getSelf().userParams?.mailAdmin) &&
				!!contact?.unsubscribed &&
				hasGroupMail,
			onClick: () => {
				if (!contact) {
					return;
				}

				openModal('Alert', {
					title: 'confirm.resub',
					body: 'confirm.resubBody',
					confirmButtonText: 'confirm.resub',
					headerIcon: 'warning',
					onClose: confirmed => {
						if (confirmed) {
							Tools.Contact.resubscribe(contact);
						}
					}
				});
			}
		},
		{
			title: T('default.assignToSeller'),
			icon: 'star',
			shouldRender: !isContact && account.userEditable && hasLeads,
			onClick: () => openModal('AssignModal', { client: account })
		},
		{
			title: T('default.resetScore'),
			icon: 'thumbs-down',
			shouldRender: !isContact && account.userEditable && !!score && hasMarketingAutomation,
			onClick: () =>
				openModal('ResetScore', {
					account: account,
					userId: AppService.getSelf().id,
					customerId: AppService.getCustomerId(),
					synch: !(contactsTotal || 0 > 1000)
				})
		},
		{
			title: T('default.uploadFile'),
			icon: 'cloud-upload',
			shouldRender: hasDocuments,
			onClick: () => {
				if (hasReactUploadFileModal) {
					return openModal(
						'UploadFileModal',
						isContact ? { contactId: contact?.id } : { accountId: account.id }
					);
				}
				Tools.$upModal.open('uploadFile', isContact ? { contactId: contact?.id } : { accountId: account.id });
			}
		},
		{
			title: T('default.eSign'),
			icon: 'edit',
			shouldRender: hasEsign && !!AppService.getEsignIntegrations().length,
			onClick: () =>
				openEditEsign(
					isContact
						? {
								esign: { client: { id: account.id }, involved: [Tools.Esign.newInvolved({ contact })] },
								openInvolved: true
						  }
						: { esign: { client: { id: account.id } } }
				)
		},
		{
			title: T('contact.exportContactData'),
			icon: 'address-card',
			shouldRender: isContact && hasExportData,
			onClick: () => {
				if (!contact) {
					return;
				}
				openModal('ExportContactData', {
					contact,
					UserDefinedObject: meta.params.UserDefinedObject
				});
			}
		},
		{
			title: T('default.activity'),
			icon: 'activity',
			shouldRender:
				account.createRights.Activity && hasActivitiesAndAppointments && hasTodoList && !hasRemoveActivities,
			onClick: () => {
				const activity = {
					client: { id: account.id },
					contacts: contact ? { id: contact.id } : undefined
				};
				Tools.$upModal.open('editActivity', { activity });
			}
		}
	];
	const actionsToRender = actions.filter(a => a.shouldRender);
	const classes = new BemClass('DashboardButtonGroup');
	const maxLengthOfComment = 65535;
	const placeholder = T('default.startTyping') + '...';
	return (
		<div className={classes.mod({ gotContent: comment }).b()} ref={wrapperRef}>
			{!isContact ? (
				<SubAccountStateFrame space="mbl" client={account} operationalAccount={account.operationalAccount} />
			) : null}
			<ButtonGroup>
				{actionsToRender.slice(0, VISIBLE_ACTION_COUNT).map((a, i) => (
					<Button key={i} onClick={a.onClick} color={a.buttonColor || 'light-grey'}>
						{a.icon ? <Icon name={a.icon as IconName} space="mrm" /> : null}
						{a.title}
					</Button>
				))}
			</ButtonGroup>
			<div className={classes.elem('content').b()}>
				<SlideFade direction="top" visible={showConfirm}>
					<CommentConfirm className={classes.elem('confirm').b()} />
				</SlideFade>
				<SlideFade direction="top" visible={!showConfirm}>
					<div className={classes.elem('comment').b()}>
						<MentionsInput
							value={comment}
							onChange={val => {
								triggerRender();
								setComment(val);
							}}
							editorRef={r => {
								textareaRef.current = r;
							}}
							editorProps={{ placeholder }}
							onEnter={() => {
								if (!showConfirm && !saving && comment) {
									saveComment();
								}
							}}
						/>
						{comment?.length > 0 ? <FakeTooltip title={T('comment.tooltip')} /> : null}
					</div>
				</SlideFade>
			</div>
			<SlideFade speed="fast" direction="top" visible={!!comment && !showConfirm && !saving}>
				<div className={classes.elem('footer').b()}>
					<div className={classes.elem('buttons').b()}>
						<Progress percentage={(comment.length / maxLengthOfComment) * 100} hideText />
						<Button onClick={saveComment}>{T('default.addComment')}</Button>
						<Button onClick={() => setComment('')} type="link" color="grey">
							{T('default.cancel')}
						</Button>
					</div>
				</div>
			</SlideFade>
			<Avatar size={28} user={AppService.getSelf()} />
		</div>
	);
};
export default DashboardButtonGroup;
